/**
 * Machine Data API Hooks (Sprint 7.1)
 * Provides React Query hooks for Machine Capacity Groups, Machines,
 * Machine Operations, and Operation Quantities.
 *
 * Endpoints from AI-Planner Sprint 7.1 (LN tirou461/462, tisfc400/401)
 */

import { useQuery } from '@tanstack/react-query';
import {
  machineCapacityGroupsApi,
  machinesApi,
  machineOperationsApi,
  machineOperationQuantitiesApi,
} from '../api/client';
import type {
  MachineCapacityGroup,
  MachineCapacityGroupListItem,
  MachineNumber,
  MachineNumberListItem,
  MachineOperation,
  MachineOperationListItem,
  MachineOperationQuantity,
  MachineStatusSummary,
  MachineCapacityType,
  MachineStatus,
} from '../types';

// ============ Query Key Constants ============

export const machineQueryKeys = {
  capacityGroups: ['machine-capacity-groups'] as const,
  capacityGroupDetail: (id: string) => ['machine-capacity-groups', id] as const,
  capacityGroupMachines: (id: string) => ['machine-capacity-groups', id, 'machines'] as const,
  machines: ['machines'] as const,
  machineDetail: (id: string) => ['machines', id] as const,
  machineOperations: (id: string) => ['machines', id, 'operations'] as const,
  machineStatusSummary: ['machines', 'status-summary'] as const,
  operations: ['machine-operations'] as const,
  operationDetail: (id: string) => ['machine-operations', id] as const,
  operationQuantities: (id: string) => ['machine-operations', id, 'quantities'] as const,
  quantities: ['machine-operation-quantities'] as const,
  quantityDetail: (id: string) => ['machine-operation-quantities', id] as const,
};

// ============ Machine Capacity Group Hooks ============

/** List all machine capacity groups with optional filters */
export function useMachineCapacityGroups(params?: {
  work_center?: string;
  capacity_type?: MachineCapacityType;
  is_active?: boolean;
  search?: string;
  ordering?: string;
}, enabled = true) {
  return useQuery({
    queryKey: [...machineQueryKeys.capacityGroups, params],
    queryFn: () => machineCapacityGroupsApi.list(params),
    enabled,
    refetchInterval: 60000,
  });
}

/** Get a single machine capacity group by ID */
export function useMachineCapacityGroup(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: machineQueryKeys.capacityGroupDetail(id!),
    queryFn: () => machineCapacityGroupsApi.get(id!),
    enabled: enabled && !!id,
  });
}

/** Get all machines within a capacity group */
export function useMachineCapacityGroupMachines(groupId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: machineQueryKeys.capacityGroupMachines(groupId!),
    queryFn: () => machineCapacityGroupsApi.getMachines(groupId!),
    enabled: enabled && !!groupId,
  });
}

// ============ Machine Number Hooks ============

/** List all machines with optional filters */
export function useMachines(params?: {
  capacity_group?: string;
  work_center?: string;
  status?: MachineStatus;
  is_active?: boolean;
  search?: string;
  ordering?: string;
}, enabled = true) {
  return useQuery({
    queryKey: [...machineQueryKeys.machines, params],
    queryFn: () => machinesApi.list(params),
    enabled,
    refetchInterval: 60000,
  });
}

/** Get a single machine by ID */
export function useMachine(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: machineQueryKeys.machineDetail(id!),
    queryFn: () => machinesApi.get(id!),
    enabled: enabled && !!id,
  });
}

/** Get all operations for a specific machine */
export function useMachineOperationsForMachine(machineId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: machineQueryKeys.machineOperations(machineId!),
    queryFn: () => machinesApi.getOperations(machineId!),
    enabled: enabled && !!machineId,
  });
}

/** Get machine status summary (count by status) */
export function useMachineStatusSummary(enabled = true) {
  return useQuery({
    queryKey: machineQueryKeys.machineStatusSummary,
    queryFn: () => machinesApi.getStatusSummary(),
    enabled,
    refetchInterval: 30000, // More frequent for status dashboard
  });
}

// ============ Machine Operation Hooks ============

/** List all machine operations with optional filters */
export function useMachineOperations(params?: {
  operation?: string;
  machine?: string;
  capacity_group?: string;
  is_active?: boolean;
  erp_order_number?: string;
  search?: string;
  ordering?: string;
}, enabled = true) {
  return useQuery({
    queryKey: [...machineQueryKeys.operations, params],
    queryFn: () => machineOperationsApi.list(params),
    enabled,
    refetchInterval: 60000,
  });
}

/** Get a single machine operation by ID */
export function useMachineOperation(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: machineQueryKeys.operationDetail(id!),
    queryFn: () => machineOperationsApi.get(id!),
    enabled: enabled && !!id,
  });
}

/** Get all quantities for a machine operation */
export function useMachineOperationQuantities(operationId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: machineQueryKeys.operationQuantities(operationId!),
    queryFn: () => machineOperationsApi.getQuantities(operationId!),
    enabled: enabled && !!operationId,
  });
}

// ============ Machine Operation Quantity Hooks ============

/** List all machine operation quantities with optional filters */
export function useAllMachineQuantities(params?: {
  machine_operation?: string;
  quantity_type?: string;
  ordering?: string;
}, enabled = true) {
  return useQuery({
    queryKey: [...machineQueryKeys.quantities, params],
    queryFn: () => machineOperationQuantitiesApi.list(params),
    enabled,
    refetchInterval: 60000,
  });
}

// ============ Composite Hooks ============

/** Combined machine dashboard data: machines list + status summary */
export function useMachineDashboard(workCenter?: string) {
  const machinesQuery = useMachines(
    workCenter ? { work_center: workCenter, is_active: true } : { is_active: true }
  );
  const statusQuery = useMachineStatusSummary();

  return {
    machines: machinesQuery.data,
    statusSummary: statusQuery.data,
    loading: machinesQuery.isLoading || statusQuery.isLoading,
    error: machinesQuery.error || statusQuery.error,
    refetch: () => {
      machinesQuery.refetch();
      statusQuery.refetch();
    },
  };
}

// ============ Helper Functions ============

/** Get display color for machine status */
export function getMachineStatusColor(status: MachineStatus): {
  bg: string;
  text: string;
  dot: string;
} {
  switch (status) {
    case 'available':
      return { bg: 'bg-green-100', text: 'text-green-800', dot: 'bg-green-500' };
    case 'in_use':
      return { bg: 'bg-blue-100', text: 'text-blue-800', dot: 'bg-blue-500' };
    case 'maintenance':
      return { bg: 'bg-yellow-100', text: 'text-yellow-800', dot: 'bg-yellow-500' };
    case 'offline':
      return { bg: 'bg-red-100', text: 'text-red-800', dot: 'bg-red-500' };
    default:
      return { bg: 'bg-gray-100', text: 'text-gray-800', dot: 'bg-gray-500' };
  }
}

/** Get display label for machine status */
export function getMachineStatusLabel(status: MachineStatus): string {
  switch (status) {
    case 'available': return 'Available';
    case 'in_use': return 'In Use';
    case 'maintenance': return 'Maintenance';
    case 'offline': return 'Offline';
    default: return status;
  }
}

/** Get display color for quantity type */
export function getQuantityTypeColor(type: string): string {
  switch (type) {
    case 'good': return 'text-green-600';
    case 'reject': return 'text-red-600';
    case 'scrap': return 'text-gray-600';
    case 'rework': return 'text-yellow-600';
    default: return 'text-gray-500';
  }
}

// Re-export types for convenience
export type {
  MachineCapacityGroup,
  MachineCapacityGroupListItem,
  MachineNumber,
  MachineNumberListItem,
  MachineOperation,
  MachineOperationListItem,
  MachineOperationQuantity,
  MachineStatusSummary,
};
